/**
 * declareSpatialColumnDialogStore — global open/close state for the
 * Declare-as-coordinates dialog (T18). Mirrors `declareEnumColumnDialogStore`:
 * any surface (grid header menu, header glyph, MetaInfo manager row) pops the
 * dialog by calling `openDeclareSpatialColumnDialog(…)` without prop-drilling,
 * and a single `GlobalDeclareSpatialColumnDialog` mount in `App.tsx` renders it.
 */

import { create } from 'zustand';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { SpatialColumnDeclaration } from '../../utils/spatialValueAccessor';

/**
 * Which UI surface triggered the dialog. Powers analytics
 * (`feature:spatial_declare { trigger }`). The three values match the mandated
 * analytics union: header context-menu, header glyph, MetaInfo manager.
 */
export type DeclareSpatialColumnDialogTrigger = 'header-menu' | 'glyph' | 'manager';

export type DeclareSpatialColumnDialogContext = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  /** Engine-specific column data type, e.g. `real`, `double precision`, `text`. */
  dataType?: string;
  engine: DatabaseEngineId;
  trigger: DeclareSpatialColumnDialogTrigger;
  /** Sibling columns in the same table — offered as the LNG column for a pair. */
  siblingColumns: string[];
  /**
   * Present when opened on an EXISTING declaration (header glyph / manager row).
   * Puts the dialog in details/remove mode instead of the declare form.
   */
  existing?: SpatialColumnDeclaration;
};

type DeclareSpatialColumnDialogState = {
  isOpen: boolean;
  context: DeclareSpatialColumnDialogContext | null;
};

type DeclareSpatialColumnDialogActions = {
  openDialog: (ctx: DeclareSpatialColumnDialogContext) => void;
  closeDialog: () => void;
};

type DeclareSpatialColumnDialogStore = DeclareSpatialColumnDialogState &
  DeclareSpatialColumnDialogActions;

export const useDeclareSpatialColumnDialogStore =
  create<DeclareSpatialColumnDialogStore>((set) => ({
    isOpen: false,
    context: null,
    openDialog: (ctx) => {
      // Same rAF defer as the enum/JSON dialogs — lets an in-flight Radix
      // overlay close finish before the Dialog mounts, preventing a stuck
      // `body { pointer-events: none }` on overlapping overlay closes.
      const open = () => set({ isOpen: true, context: ctx });
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(open);
      } else {
        setTimeout(open, 0);
      }
    },
    closeDialog: () => set({ isOpen: false, context: null }),
  }));

export const openDeclareSpatialColumnDialog = (
  ctx: DeclareSpatialColumnDialogContext,
) => useDeclareSpatialColumnDialogStore.getState().openDialog(ctx);
